import {
  queryOptions,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useRef } from 'react';

import type {
  AnnotationSchema,
  AnnotationSet,
  DatasetVersion,
} from '../../../../../services/dataHubService.interface.js';
import type { DataHubService } from '../../../../../services/dataHubService.js';
import { withCorrelationId } from '../../../../../services/usageContext.js';
import { useUserBi } from '../../../../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  EntityType,
  OperationType,
  PanelType,
} from '../../../../../telemetry/userbi.js';
import type { AnnotationTypeDef } from '../annotationTypes/annotationType.interface.js';
import { ANNOTATION_TYPES } from '../annotationTypes/registry.js';
import type { AnnotationTarget } from '../save/annotationDocument.js';
import {
  assertTargetFile,
  discoverDocuments,
  readDocumentMetadata,
} from '../save/annotationDocument.js';
import {
  allPages,
  AnnotationSaveRecoveryError,
  ensureAnnotationItem,
  LOOKUP_PAGE_SIZE,
  listAnnotationFileStates,
  preflightAnnotationPublish,
  readTargetItem,
  recoverAnnotationSave,
  requireDraft,
  resolveExpectedActionSchema,
  saveExpectedActions,
} from '../save/expectedActionStore.js';
import type { AnnotationService } from '../save/expectedActionStore.js';

const AUTHORING_QUERY_KEY = 'annotation-authoring';
export type AnnotationAuthoringService = AnnotationService &
  Pick<
    DataHubService,
    'getDataset' | 'getDataEntry' | 'listDatasetVersions' | 'listDataEntries'
  >;
export function registeredSchema(
  schema: AnnotationSchema,
): AnnotationTypeDef | undefined {
  return ANNOTATION_TYPES.find(
    (type) =>
      type.schemaName === schema.name &&
      type.schemaDefinition === schema.schema,
  );
}
async function readSourceVersions(
  service: AnnotationAuthoringService,
  dataSetId: string,
) {
  const [dataset, history] = await Promise.all([
    service.getDataset(dataSetId),
    allPages((continuationToken) =>
      service.listDatasetVersions({
        dataSetId,
        pageSize: LOOKUP_PAGE_SIZE,
        continuationToken,
      }),
    ),
  ]);
  // TEDH version history excludes the current Draft.
  const versions: DatasetVersion[] = [
    dataset,
    ...history.filter((entry) => entry.version !== dataset.version),
  ];
  return { dataset, versions };
}
export function annotationCatalogOptions(
  service: AnnotationAuthoringService,
  dataSetId: string,
) {
  return queryOptions({
    queryKey: [AUTHORING_QUERY_KEY, dataSetId, 'catalog'],
    queryFn: async () => {
      const [{ dataset, versions }, sets, schemas] = await Promise.all([
        readSourceVersions(service, dataSetId),
        allPages((continuationToken) =>
          service.listAnnotationSets({
            dataSetId,
            pageSize: LOOKUP_PAGE_SIZE,
            continuationToken,
          }),
        ),
        allPages((continuationToken) =>
          service.listAnnotationSchemas({
            pageSize: LOOKUP_PAGE_SIZE,
            continuationToken,
          }),
        ),
      ]);
      return {
        dataset,
        versions,
        sets,
        schemas,
      };
    },
    enabled: dataSetId !== '',
  });
}
export function useAnnotationCatalog(
  service: AnnotationAuthoringService,
  datasetId: string,
) {
  return useQuery(annotationCatalogOptions(service, datasetId));
}
export function useAnnotationFileStates(
  service: AnnotationService,
  dataSetId: string,
) {
  return useQuery({
    queryKey: [AUTHORING_QUERY_KEY, dataSetId, 'file-states'],
    queryFn: () => listAnnotationFileStates({ service, dataSetId }),
    enabled: dataSetId !== '',
  });
}
export function useAnnotationSetupPending() {
  return useIsMutating({ mutationKey: [AUTHORING_QUERY_KEY] }) > 0;
}
export function useAnnotationSources(
  service: AnnotationAuthoringService,
  dataSetId: string,
  version: number,
) {
  return useQuery({
    queryKey: [AUTHORING_QUERY_KEY, dataSetId, 'sources', version],
    queryFn: () =>
      allPages((continuationToken) =>
        service.listDataEntries({
          dataSetId,
          dataSetVersion: version,
          pageSize: LOOKUP_PAGE_SIZE,
          continuationToken,
        }),
      ),
    enabled: version > 0,
  });
}
export function useAnnotationSourceDetail(
  service: AnnotationAuthoringService,
  dataSetId: string,
  version: number,
  dataId: string,
) {
  return useQuery({
    queryKey: [AUTHORING_QUERY_KEY, dataSetId, 'source', version, dataId],
    queryFn: () => service.getDataEntry(dataSetId, dataId, version),
    enabled: version > 0 && dataId !== '',
  });
}
export function useAnnotationDocuments(
  service: AnnotationAuthoringService,
  dataSetId: string,
  set: AnnotationSet | undefined,
  sourceDataId: string,
) {
  return useQuery({
    queryKey: [
      AUTHORING_QUERY_KEY,
      dataSetId,
      'documents',
      set?.annotationSetId,
      set?.version,
      sourceDataId,
    ],
    queryFn: () => {
      if (set === undefined) throw new Error('Select an annotation set.');
      return service.annotationReadSourceItems(
        dataSetId,
        set.annotationSetId,
        set.version,
        [sourceDataId],
      );
    },
    enabled: set !== undefined && sourceDataId !== '',
  });
}
export function useAnnotationEditorTarget(
  service: AnnotationAuthoringService,
  target: AnnotationTarget | undefined,
) {
  return useQuery({
    queryKey: ['annotation-editor-target', target],
    queryFn: async () => {
      if (target === undefined) throw new Error('Select an annotation target.');
      const [item, { versions }, schemas] = await Promise.all([
        readTargetItem(service, target),
        readSourceVersions(service, target.dataSetId),
        allPages((continuationToken) =>
          service.listAnnotationSchemas({
            pageSize: LOOKUP_PAGE_SIZE,
            continuationToken,
          }),
        ),
      ]);
      if (
        !versions.some(
          (version) => version.version === target.sourceDatasetVersion,
        )
      )
        throw new Error(
          'The selected source dataset version is no longer available. Return to setup.',
        );
      const schema = schemas.find((value) => value.id === target.schemaId);
      if (
        schema === undefined ||
        registeredSchema(schema)?.kind !== target.kind
      )
        throw new Error('The selected schema has no registered editor.');
      const file = discoverDocuments(item.dataFiles ?? []).find(
        (value) =>
          readDocumentMetadata(value)?.documentId === target.documentId ||
          (target.legacy && value.id === target.documentId),
      );
      if (file === undefined)
        throw new Error('The selected document is missing.');
      if (readDocumentMetadata(file) === undefined) {
        const draft = await requireDraft(service, target);
        if (
          (item.properties?.rawDataFileId ??
            draft.properties?.rawDataFileId) !== target.sourceFileId
        )
          throw new Error(
            'This legacy document cannot be associated with the selected source file.',
          );
      }
      assertTargetFile(target, file);
      return {
        ...target,
        fileId: file.id,
        fileName: file.fileName ?? target.fileName,
      };
    },
    enabled: target !== undefined,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export interface StartAnnotationInput {
  dataSetId: string;
  sourceDataId: string;
  sourceFileId: string;
  sourceDatasetVersion: number;
  selectedSet?: AnnotationSet;
  newSetName: string;
  schema: AnnotationSchema;
  type: AnnotationTypeDef;
  fileId?: string;
  newFileName: string;
}
interface StartProgress {
  draft?: AnnotationSet;
  target?: AnnotationTarget;
  completed?: AnnotationTarget;
  recovery?: AnnotationSaveRecoveryError;
}

export function useStartAnnotationDocument(
  service: AnnotationAuthoringService,
) {
  const client = useQueryClient();
  const { trackOperation } = useUserBi();
  const progress = useRef(new Map<string, StartProgress>());
  return useMutation({
    mutationKey: [AUTHORING_QUERY_KEY],
    mutationFn: (input: StartAnnotationInput) =>
      trackOperation(
        {
          panelType: PanelType.AnnotationEditorPage,
          entityType: EntityType.AnnotationData,
          operation: OperationType.Create,
        },
        async (correlationId) => {
          const usage = { extras: withCorrelationId(correlationId) };
          const key = JSON.stringify([
            input.dataSetId,
            input.sourceDataId,
            input.sourceFileId,
            input.sourceDatasetVersion,
            input.selectedSet?.annotationSetId,
            input.selectedSet?.version,
            input.newSetName,
            input.schema.id,
            input.fileId,
            input.newFileName,
          ]);
          const state = progress.current.get(key) ?? {};
          progress.current.set(key, state);
          const { versions } = await readSourceVersions(
            service,
            input.dataSetId,
          );
          if (
            !versions.some(
              (version) => version.version === input.sourceDatasetVersion,
            )
          )
            throw new Error('Select an available source dataset version.');
          const source = await service.getDataEntry(
            input.dataSetId,
            input.sourceDataId,
            input.sourceDatasetVersion,
          );
          if (!source.dataFiles?.some((file) => file.id === input.sourceFileId))
            throw new Error(
              'The selected source file does not exist in this version.',
            );
          if (registeredSchema(input.schema)?.kind !== input.type.kind)
            throw new Error('The selected schema has no supported editor.');
          if (state.completed !== undefined) {
            await requireDraft(service, state.completed);
            return state.completed;
          }
          if (state.recovery !== undefined) {
            state.completed = await recoverAnnotationSave(
              service,
              state.recovery,
              usage,
            );
            state.recovery = undefined;
            return state.completed;
          }
          if (state.draft === undefined) {
            const set = input.selectedSet;
            state.draft =
              set === undefined
                ? await service.createAnnotationSet(
                    input.dataSetId,
                    {
                      schemaId: input.schema.id,
                      name: input.newSetName.trim(),
                    },
                    usage,
                  )
                : set.status === 'Draft'
                  ? set
                  : await service.createAnnotationSetDraft(
                      input.dataSetId,
                      set.annotationSetId,
                    );
          }
          const draft = state.draft;
          const item = await ensureAnnotationItem(
            service,
            input.dataSetId,
            draft,
            input.sourceDataId,
            source.name ?? input.sourceDataId,
            usage,
          );
          const files = item.dataFiles ?? [];
          const selected =
            input.fileId === undefined
              ? undefined
              : discoverDocuments(files).find(
                  (file) => file.id === input.fileId,
                );
          if (input.fileId !== undefined && selected === undefined)
            throw new Error(
              'The selected annotation file is no longer available.',
            );
          const metadata =
            selected === undefined ? undefined : readDocumentMetadata(selected);
          const legacySourceId =
            item.properties?.rawDataFileId ?? draft.properties?.rawDataFileId;
          if (
            selected !== undefined &&
            metadata === undefined &&
            legacySourceId !== input.sourceFileId
          ) {
            throw new Error(
              'This unmanaged file cannot be associated with the selected source. Use the set manager to inspect it.',
            );
          }
          const target: AnnotationTarget = state.target ?? {
            dataSetId: input.dataSetId,
            sourceDataId: input.sourceDataId,
            sourceFileId: input.sourceFileId,
            sourceDatasetVersion: input.sourceDatasetVersion,
            annotationSetId: draft.annotationSetId,
            draftVersion: draft.version,
            annotationDataId: item.dataId,
            schemaId: input.schema.id,
            kind: input.type.kind,
            documentId:
              metadata?.documentId ?? selected?.id ?? crypto.randomUUID(),
            fileName: selected?.fileName ?? input.newFileName.trim(),
            fileId: selected?.id,
            legacy: selected !== undefined && metadata === undefined,
          };
          state.target = target;
          if (selected !== undefined) {
            assertTargetFile(target, selected);
            const predecessor = files.find(
              (file) => file.id === metadata?.supersedesFileId,
            );
            state.completed =
              predecessor === undefined
                ? target
                : await recoverAnnotationSave(
                    service,
                    new AnnotationSaveRecoveryError(
                      'Resume cleanup',
                      {
                        ...target,
                        fileId: predecessor.id,
                        legacy: readDocumentMetadata(predecessor) === undefined,
                      },
                      selected.id,
                      metadata!.saveOperationId,
                    ),
                    usage,
                  );
            return state.completed;
          }
          try {
            state.completed = await saveExpectedActions({
              service,
              target,
              actions: [],
              annotationType: input.type,
              usage,
            });
            return state.completed;
          } catch (error) {
            if (error instanceof AnnotationSaveRecoveryError)
              state.recovery = error;
            throw error;
          }
        },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: [AUTHORING_QUERY_KEY] });
    },
  });
}

export function usePublishAnnotationDraft(service: AnnotationAuthoringService) {
  const client = useQueryClient();
  const { trackOperation } = useUserBi();
  return useMutation({
    mutationKey: [AUTHORING_QUERY_KEY],
    mutationFn: (input: { dataSetId: string; set: AnnotationSet }) =>
      trackOperation(
        {
          panelType: PanelType.AnnotationEditorPage,
          entityType: EntityType.AnnotationSet,
          operation: OperationType.Publish,
        },
        async (correlationId) => {
          await preflightAnnotationPublish(
            service,
            input.dataSetId,
            input.set.annotationSetId,
            input.set.version,
          );
          return service.publishAnnotationSet(
            input.dataSetId,
            input.set.annotationSetId,
            { extras: withCorrelationId(correlationId) },
          );
        },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: [AUTHORING_QUERY_KEY] });
    },
  });
}
export function useRegisterAnnotationSchema(
  service: AnnotationAuthoringService,
) {
  const client = useQueryClient();
  return useMutation({
    mutationKey: [AUTHORING_QUERY_KEY],
    mutationFn: () => resolveExpectedActionSchema(service),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: [AUTHORING_QUERY_KEY] });
    },
  });
}
